import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    AutoHideOnScroll,
    EventCardSkeleton,
    EventDisplayMode,
    GridLoadingState,
    Pagination,
} from '@/components';
import { useInfiniteScroll, useScrollRestore } from '@/hooks';
import { ResultsSummary } from '@/features/QueryResults/components/ResultsSummary';
import { cn } from '@/lib/utils';
import { CARD_GRID_COLUMNS_CLASSES } from '@/constants/layout';

export type EventsListPagination =
    | {
          mode: 'paginated';
          page: number;
          pageSize: number;
          totalCount: number;
          isFetching: boolean;
          onPageChange: (page: number) => void;
      }
    | {
          mode: 'loadMore';
          isLoadingMore: boolean;
          hasMore: boolean;
          onLoadMore: () => void;
          loadingMessage?: string;
          allLoadedMessage?: string;
      };

interface EventsListLayoutProps {
    eventCount: number;
    isEmpty: boolean;
    sourceCount?: number;
    query?: string | null;
    isLoading: boolean;
    pagination: EventsListPagination;
    actionBar: ReactNode;
    emptyMessage?: string;
    displayMode?: EventDisplayMode;
    pageKey?: string;
    children: ReactNode;
}

export function EventsListLayout({
    eventCount,
    isEmpty,
    sourceCount,
    query = null,
    isLoading,
    pagination,
    actionBar,
    emptyMessage,
    displayMode = 'events',
    pageKey,
    children,
}: EventsListLayoutProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);

    const isPaginated = pagination.mode === 'paginated';
    const page = pagination.mode === 'paginated' ? pagination.page : 1;

    useScrollRestore({
        pageKey: isPaginated ? undefined : pageKey,
        scrollContainer,
        isReady: !isLoading,
    });

    const isFetching = pagination.mode === 'paginated' && pagination.isFetching;
    const previousPage = useRef(page);
    const settledPage = useRef(page);

    useEffect(() => {
        if (isPaginated && previousPage.current !== page) {
            scrollContainer?.scrollTo({ top: 0 });
        }
        previousPage.current = page;
    }, [isPaginated, page, scrollContainer]);

    useEffect(() => {
        if (!isPaginated || !scrollContainer || isFetching) return;
        if (settledPage.current === page) return;
        settledPage.current = page;
        const frameId = requestAnimationFrame(() => {
            scrollContainer.scrollTo({ top: 0 });
        });
        return () => cancelAnimationFrame(frameId);
    }, [isPaginated, page, isFetching, scrollContainer]);

    useInfiniteScroll(scrollContainer, {
        enabled: pagination.mode === 'loadMore',
        onLoadMore:
            pagination.mode === 'loadMore' ? pagination.onLoadMore : undefined,
        isLoading:
            pagination.mode === 'loadMore'
                ? pagination.isLoadingMore || isLoading
                : false,
        hasMore: pagination.mode === 'loadMore' ? pagination.hasMore : false,
        threshold: 200,
    });

    const gridClassName = cn(
        'grid gap-4',
        CARD_GRID_COLUMNS_CLASSES,
        pagination.mode === 'loadMore' && 'pb-40',
    );

    const defaultEmptyMessage = t('page.noResults');
    const isLoadingMore =
        pagination.mode === 'loadMore' && pagination.isLoadingMore;
    const isReachingEnd = pagination.mode === 'loadMore' && !pagination.hasMore;
    const showPagination =
        pagination.mode === 'paginated' &&
        pagination.totalCount > pagination.pageSize;

    return (
        <div className="h-full flex flex-col items-center relative">
            <AutoHideOnScroll container={scrollContainer}>
                {actionBar}
            </AutoHideOnScroll>

            <div
                ref={setScrollContainer}
                className="w-full flex flex-col flex-1 min-h-0 overflow-y-auto scrollbar-hide relative z-0 px-4 [overflow-anchor:none]"
            >
                <div className="w-full max-w-content mx-auto flex flex-col pb-24">
                    <div className="flex-shrink-0 mb-2 pt-24">
                        {!isLoading && !isEmpty && (
                            <ResultsSummary
                                eventCount={eventCount}
                                sourceCount={sourceCount}
                                query={query}
                                displayMode={displayMode}
                            />
                        )}
                    </div>

                    <GridLoadingState
                        isLoading={isLoading}
                        isEmpty={isEmpty}
                        isLoadingMore={isLoadingMore}
                        isReachingEnd={isReachingEnd}
                        emptyMessage={emptyMessage || defaultEmptyMessage}
                        loadingMoreMessage={
                            pagination.mode === 'loadMore'
                                ? pagination.loadingMessage
                                : undefined
                        }
                        endMessage={
                            pagination.mode === 'loadMore'
                                ? pagination.allLoadedMessage
                                : undefined
                        }
                        skeletonComponent={<EventCardSkeleton />}
                        skeletonCount={9}
                        gridClassName={gridClassName}
                    >
                        {children}
                    </GridLoadingState>

                    {showPagination && pagination.mode === 'paginated' && (
                        <div className="flex justify-center pt-8 pb-12">
                            <Pagination
                                page={pagination.page}
                                pageSize={pagination.pageSize}
                                totalCount={pagination.totalCount}
                                isLoading={pagination.isFetching}
                                hasMore={
                                    pagination.page <
                                    Math.ceil(
                                        pagination.totalCount /
                                            pagination.pageSize,
                                    )
                                }
                                onPageChange={pagination.onPageChange}
                            />
                        </div>
                    )}
                </div>
            </div>

            {pagination.mode === 'loadMore' && (
                <div className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none bg-gradient-to-t from-grey-700 to-transparent" />
            )}
        </div>
    );
}
